import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectClient, selectClientIsLoading } from '@client/model/selectors';
import { putBatchSelfFundingExistingAssetsValueForClientByClientIdRequest } from '@client/model/clientModel';
import { calculateTotalPolicyPremiumCost } from '@client/util/fundingSufficiency';
import { calculatePremiumDeductions } from '@client/util/ltcFinancialPlanning';

/**
 * Hook to apply insurance premium deductions to self-funding sources.
 * Calculates which assets to deduct from and dispatches a batch update action.
 *
 * @returns Object with applyDeductions function and isSaving state
 */
export function useApplyPremiumDeductions() {
  const dispatch = useDispatch();
  const client = useSelector(selectClient);
  const isSaving = useSelector(selectClientIsLoading);

  const applyDeductions = useCallback(() => {
    if (!client?.multipleFundingSources) {
      console.warn('No funding sources available to apply deductions');
      return;
    }

    // Calculate total policy cost for shop insurance policies
    const totalPolicyCost = calculateTotalPolicyPremiumCost(client.multipleFundingSources);

    if (totalPolicyCost <= 0) {
      console.warn('No policy cost to deduct');
      return;
    }

    // Calculate premium deductions using shared utility
    const deductions = calculatePremiumDeductions(client.multipleFundingSources, totalPolicyCost);

    if (deductions.length === 0) {
      console.warn('No deductions calculated - possibly insufficient self-funding assets');
      return;
    }

    // Map to API update format
    const updates = deductions.map(d => ({
      fundingSourceId: d.fundingSourceId,
      existingAssetsValue: d.newValue,
    }));

    // Dispatch batch update action
    dispatch(
      putBatchSelfFundingExistingAssetsValueForClientByClientIdRequest({
        clientId: client.clientId,
        updates,
      }),
    );
  }, [dispatch, client]);

  return {
    applyDeductions,
    isSaving,
  };
}

